import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Grid,
  Header,
  Segment,
  Icon,
  Button,
  Message,
  Breadcrumb,
  Label
} from 'semantic-ui-react';
import ProfileManager from '../../components/ProfileManager';
import { AuthStdFooter } from "../../utils/auth-std-footer";
import '../../styles/landing-pages.css';
import '../../styles/article.css';
import userPuzzle from '../../assets/userPuzzle.svg';
import apiClient from '../../apiTools/apiClient';
import useRoleNormalization from '../../hooks/useRoleNormalization';

const AdminUserProfileEditor = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user.profile);
  const isAdmin = currentUser?.data?.roles?.includes('admin');

  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize role normalization hook
  const {
    normalizeRole,
    isValidRole,
    extractRoleFromProfileType
  } = useRoleNormalization();

  useEffect(() => {
    // Verify admin access
    if (!isAdmin) {
      setError('Admin access required');
      setLoading(false);
      return;
    }

    // Fetch user information
    fetchUserInfo();
  }, [userId, isAdmin]);

  /**
   * Extract user role from API response data
   * Handles both User objects (with primaryRole) and Profile objects (with type/profileType)
   */
  const extractUserRole = (data, userId) => {
    // Find all data for this user
    const userData = data.filter(item => item.userId === userId);

    if (userData.length === 0) {
      console.error('No data found for user:', userId);
      return null;
    }

    console.log(`Found ${userData.length} items for user ${userId}:`, userData);

    // Priority 1: Check for User object with primaryRole
    const userObject = userData.find(item => item.primaryRole && !item.profileType);
    if (userObject && userObject.primaryRole) {
      const normalized = normalizeRole(userObject.primaryRole);
      console.log(`Found role from User object: ${userObject.primaryRole} -> ${normalized}`);
      if (isValidRole(normalized)) {
        return normalized;
      }
    }

    // Priority 2: Check Profile objects
    const profiles = userData.filter(item => item.profileType || item.type);
    if (profiles.length > 0) {
      console.log(`Found ${profiles.length} profiles for user`);

      // Use primary profile if marked
      const primaryProfile = profiles.find(p => p.isPrimary);
      if (primaryProfile) {
        const roleFromType = primaryProfile.type ||
          extractRoleFromProfileType(primaryProfile.profileType);
        const normalized = normalizeRole(roleFromType);
        console.log(`Primary profile role: ${roleFromType} -> ${normalized}`);
        if (isValidRole(normalized)) {
          return normalized;
        }
      }

      // Otherwise use first valid profile
      for (const profile of profiles) {
        const roleFromType = profile.type ||
          extractRoleFromProfileType(profile.profileType);
        const normalized = normalizeRole(roleFromType);
        console.log(`Profile role: ${roleFromType} -> ${normalized}`);
        if (isValidRole(normalized)) {
          return normalized;
        }
      }
    }

    console.error('Could not determine valid role from data');
    return null; // No valid role found
  };

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch basic user info
      const response = await apiClient.get('/admin/users');
      const data = response.data;

      console.log('=== ADMIN USER PROFILE EDITOR - API RESPONSE ===');
      console.log('API response data:', data);

      if (!Array.isArray(data)) {
        console.error('Unexpected API response format:', data);
        setError('Invalid API response format');
        return;
      }

      // Extract the user's role using our comprehensive function
      const normalizedRole = extractUserRole(data, userId);

      if (!normalizedRole) {
        console.error('Could not determine user role');
        setError('Unable to determine user role');
        return;
      }

      // Get user info (prefer User object, then primary profile, then any profile)
      const userData = data.filter(item => item.userId === userId);

      // Find the best user info object
      let userInfo = null;

      // Try to find User object first
      userInfo = userData.find(item => item.primaryRole && !item.profileType);

      // If no User object, use primary profile
      if (!userInfo) {
        userInfo = userData.find(item => item.isPrimary === true);
      }

      // If no primary profile, use first profile
      if (!userInfo && userData.length > 0) {
        userInfo = userData[0];
      }

      if (userInfo) {
        const enhancedUser = {
          ...userInfo,
          normalizedRole
        };

        console.log('Enhanced user with normalizedRole:', enhancedUser);
        setUserInfo(enhancedUser);
      } else {
        setError('User not found');
      }
    } catch (err) {
      console.error('Error fetching user info:', err);
      setError('Failed to load user information');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <Container fluid className="landing-page admin-landing">
        <Container style={{ marginTop: "30px" }}>
          <Message negative>
            <Message.Header>Access Denied</Message.Header>
            <p>You must be an administrator to access this page.</p>
            <Button primary onClick={() => navigate('/admin/user-management')}>
              Back to User Management
            </Button>
          </Message>
        </Container>
      </Container>
    );
  }

  return (
    <Container fluid className="landing-page admin-landing">
      <Container style={{ marginTop: "30px" }}>
        <Grid className="articleListWidth">
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="img-with-gradient">
                <img src={userPuzzle} alt="" style={{ width: '120px', height: '120px' }} />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">Edit User Profile</span>
              <span className="blgListSubTitle">Administrator Profile Management</span>
              {userInfo && (
                <div style={{ marginTop: '1em' }}>
                  <Label size="large" color="blue">
                    <Icon name="user" />
                    Editing: {userInfo.name || userInfo.email}
                  </Label>
                  <Label size="large">
                    <Icon name="mail" />
                    {userInfo.email}
                  </Label>
                  {userInfo.phone && (
                    <Label size="large">
                      <Icon name="phone" />
                      {userInfo.phone}
                    </Label>
                  )}
                </div>
              )}
              <p className="landing-subheading-text" style={{ marginTop: '1em' }}>
                You are editing another user's profile as an administrator.
                Changes will be saved to their account directly.
              </p>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid className="articleListWidth" style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}></Grid>
      </Container>

      <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
        {/* Breadcrumb navigation */}
        <Breadcrumb size="large" style={{ marginBottom: '2em' }}>
          <Breadcrumb.Section link onClick={() => navigate('/admin/user-management')}>
            <Icon name="users" /> User Management
          </Breadcrumb.Section>
          <Breadcrumb.Divider />
          <Breadcrumb.Section active>
            <Icon name="edit" /> Edit Profile
          </Breadcrumb.Section>
        </Breadcrumb>

        {/* Admin Notice */}
        <Message info icon>
          <Icon name="shield" />
          <Message.Content>
            <Message.Header>Administrator Mode</Message.Header>
            <p>
              You are editing this user's profile as an administrator.
              All changes will be saved directly to their account.
              Please ensure you have authorization to make these changes.
            </p>
          </Message.Content>
        </Message>

        {/* Error handling */}
        {error && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{error}</p>
            <Button primary onClick={() => navigate('/admin/user-management')}>
              Back to User Management
            </Button>
          </Message>
        )}

        {/* Loading state */}
        {/* {loading && (
          <Segment loading>
            <p style={{ height: '200px' }}>Loading user information...</p>
          </Segment>
        )} */}

        {/* Profile Editor */}
        {!loading && !error && userInfo && (
          <div>
            {/* Action buttons */}
            <div style={{ marginBottom: '2em' }}>
              <Button
                icon="arrow left"
                labelPosition="left"
                content="Back to User Management"
                onClick={() => navigate('/admin/user-management')}
              />
              <Button
                icon="user"
                labelPosition="left"
                content="View in User List"
                color="blue"
                onClick={() => navigate('/admin/user-management', {
                  state: { highlightUserId: userId }
                })}
                style={{ marginLeft: '1em' }}
              />
            </div>

            {/* ProfileManager Component */}
            <Segment raised>
              <Header as="h2" dividing>
                <Icon name="id card outline" />
                <Header.Content>
                  Profile Details
                  <Header.Subheader>
                    Managing profiles for {userInfo.name || 'User'}
                  </Header.Subheader>
                </Header.Content>
              </Header>

              {/* Pass the userId, role, and initial data to ProfileManager */}
              {userInfo.normalizedRole ? (
                <ProfileManager
                  userId={userId}
                  userRole={userInfo.normalizedRole}
                  initialUserData={{
                    firstName: userInfo.firstName || userInfo.name?.split(' ')[0] || '',
                    lastName: userInfo.lastName || userInfo.name?.split(' ').slice(1).join(' ') || '',
                    phone: userInfo.phone || '',
                    email: userInfo.email || ''
                  }}
                />
              ) : (
                <Message warning>
                  <Message.Header>Role Detection Issue</Message.Header>
                  <p>Unable to determine user role. Please check the user data and try refreshing.</p>
                </Message>
              )}
            </Segment>

            {/* Additional admin info */}
            <Segment secondary>
              <Header as="h4">
                <Icon name="info circle" />
                User Information
              </Header>
              <Grid columns={3} divided>
                <Grid.Column>
                  <p><strong>User ID:</strong><br />{userId}</p>
                </Grid.Column>
                <Grid.Column>
                  <p><strong>Primary Role:</strong><br />
                    {userInfo.normalizedRole === 'admin' && 'Administrator'}
                    {userInfo.normalizedRole === 'bookCreator' && 'Book Creator'}
                    {userInfo.normalizedRole === 'voiceActor' && 'Voice Actor'}
                    {!userInfo.normalizedRole && 'Not set'}
                  </p>
                </Grid.Column>
                <Grid.Column>
                  <p><strong>Created:</strong><br />
                    {userInfo.createdAt ? new Date(userInfo.createdAt).toLocaleDateString() : 'Unknown'}
                  </p>
                </Grid.Column>
              </Grid>
            </Segment>
          </div>
        )}
      </Container>

      {AuthStdFooter()}
    </Container>
  );
};

export default AdminUserProfileEditor;
